import React from 'react';
import { formatSearchVolume, getSearchVolumeStyle } from '@/shared/utils/constants';
import ComingSoonCell from './ComingSoonCell';

interface CellRendererParams {
  value: number | null | undefined;
  data?: {
    keyword?: string;
    [key: string]: any;
  };
  node?: {
    id?: string;
    rowIndex?: number;
  };
  context?: {
    openModalId?: string | null;
    setOpenModalId?: (id: string | null) => void;
    language?: 'fa' | 'en';
    isDark?: boolean;
  };
}

const SearchVolumeCellRenderer = (params: CellRendererParams) => {
  const value = params.value;
  const rowData = params.data;
  const openModalId = params.context?.openModalId;
  const setOpenModalId = params.context?.setOpenModalId;
  const language = params.context?.language || 'fa';
  const isDark = params.context?.isDark || document.documentElement.classList.contains('dark');
  // Use node.id which comes from getRowId (which uses keyword)
  // This ensures consistency with ag-grid's row identification
  const rowId = params.node?.id || rowData?.keyword || `row-${params.node?.rowIndex}`;
  const modalId = `sv-modal-${rowId}`;
  const isModalOpen = openModalId === modalId;
  
  if (!value || value === 0) {
    return <ComingSoonCell language={language} isDark={isDark} />;
  }

  const formattedValue = formatSearchVolume(value);
  const style = getSearchVolumeStyle(value);
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (setOpenModalId) {
      setOpenModalId(isModalOpen ? null : modalId);
    }
  };

  // Generate tooltip HTML content with dark mode support
  const bgColor = isDark ? '#ffffff' : 'rgb(31, 41, 55)';
  const textColor = isDark ? 'rgb(31, 41, 55)' : 'white';
  const borderColor = isDark ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)';
  const labelColor = isDark ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)';

  // All color ranges
  const colorRanges = [
    {
      color: '#dc2626',
      label: language === 'fa' ? 'کم (قرمز)' : 'Low (Red)',
      range: language === 'fa' ? 'زیر ۱,۰۰۰' : 'Under 1,000'
    },
    {
      color: '#7c3aed',
      label: language === 'fa' ? 'متوسط (بنفش)' : 'Medium (Purple)',
      range: language === 'fa' ? '۱,۰۰۰ - ۹,۹۹۹' : '1,000 - 9,999'
    },
    {
      color: '#0284c7',
      label: language === 'fa' ? 'متوسط-زیاد (آبی)' : 'Medium-High (Blue)',
      range: language === 'fa' ? '۱۰,۰۰۰ - ۹۹,۹۹۹' : '10,000 - 99,999'
    },
    {
      color: '#059669',
      label: language === 'fa' ? 'زیاد (سبز)' : 'High (Green)',
      range: language === 'fa' ? '۱۰۰,۰۰۰+' : '100,000+'
    }
  ];

  const rangesHTML = colorRanges.map((range, index) => `
    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: ${index < colorRanges.length - 1 ? '8px' : '0'};">
      <div style="width: 12px; height: 12px; background-color: ${range.color}; border-radius: 2px;"></div>
      <div style="flex: 1;">
        <div style="color: ${textColor}; font-weight: 500; font-size: 13px; margin-bottom: 2px;">
          ${range.label}
        </div>
        <div style="color: ${labelColor}; font-size: 11px; line-height: 1.4;">
          ${range.range}
        </div>
      </div>
    </div>
  `).join('');

  const tooltipHTML = `
    <div style="background-color: ${bgColor}; color: ${textColor}; padding: 12px; border-radius: 8px; border: 1px solid ${borderColor}; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); min-width: 220px;">
      <div style="font-weight: 600; margin-bottom: 12px; font-size: 13px; color: ${labelColor}; text-transform: uppercase; letter-spacing: 0.5px;">
        ${language === 'fa' ? 'حجم جستجو' : 'Search Volume'}
      </div>
      ${rangesHTML}
      <div style="color: ${labelColor}; font-size: 11px; line-height: 1.4; border-top: 1px solid ${borderColor}; padding-top: 8px; margin-top: 12px;">
        ${language === 'fa' ? 'کلیک کنید تا روند جستجوی یک سال اخیر را ببینید' : 'Click to view 1-year search trend'}
      </div>
    </div>
  `;

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const currentIsDark = document.documentElement.classList.contains('dark');
    const updatedBgColor = currentIsDark ? '#ffffff' : 'rgb(31, 41, 55)';
    const updatedTextColor = currentIsDark ? 'rgb(31, 41, 55)' : 'white';
    const updatedBorderColor = currentIsDark ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)';
    const updatedLabelColor = currentIsDark ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)';

    const updatedRangesHTML = colorRanges.map((range, index) => `
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: ${index < colorRanges.length - 1 ? '8px' : '0'};">
        <div style="width: 12px; height: 12px; background-color: ${range.color}; border-radius: 2px;"></div>
        <div style="flex: 1;">
          <div style="color: ${updatedTextColor}; font-weight: 500; font-size: 13px; margin-bottom: 2px;">
            ${range.label}
          </div>
          <div style="color: ${updatedLabelColor}; font-size: 11px; line-height: 1.4;">
            ${range.range}
          </div>
        </div>
      </div>
    `).join('');

    const updatedTooltipHTML = `
      <div style="background-color: ${updatedBgColor}; color: ${updatedTextColor}; padding: 12px; border-radius: 8px; border: 1px solid ${updatedBorderColor}; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); min-width: 220px;">
        <div style="font-weight: 600; margin-bottom: 12px; font-size: 13px; color: ${updatedLabelColor}; text-transform: uppercase; letter-spacing: 0.5px;">
          ${language === 'fa' ? 'حجم جستجو' : 'Search Volume'}
        </div>
        ${updatedRangesHTML}
        <div style="color: ${updatedLabelColor}; font-size: 11px; line-height: 1.4; border-top: 1px solid ${updatedBorderColor}; padding-top: 8px; margin-top: 12px;">
          ${language === 'fa' ? 'کلیک کنید تا روند جستجوی یک سال اخیر را ببینید' : 'Click to view 1-year search trend'}
        </div>
      </div>
    `;
    
    e.currentTarget.setAttribute('data-tooltip-html', updatedTooltipHTML);
  };
  
  return (
    <>
      <div 
        data-tooltip-id="search-volume-tooltip-global"
        data-tooltip-place="top"
        data-tooltip-html={tooltipHTML}
        onMouseEnter={handleMouseEnter}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          height: '100%',
          padding: '0',
          cursor: 'pointer'
        }}
        onClick={handleClick}
      >
        {/* Search Icon */}
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke={style.color} 
          strokeWidth="2"
          style={{ flexShrink: 0 }}
        >
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        
        {/* Number */}
        <span
          style={{
            color: style.color,
            fontWeight: '700',
            fontSize: '13px',
            minWidth: '45px',
            textAlign: 'left',
            marginTop: '4px'
          }}
        >
          {formattedValue}
        </span>
        
        {/* Mini Bar Chart */}
        <div style={{
          flex: 1,
          height: '6px',
          backgroundColor: '#e5e7eb',
          borderRadius: '3px',
          overflow: 'hidden',
          minWidth: '40px',
          maxWidth: '80px'
        }}>
          <div style={{
            height: '100%',
            width: `${style.percentage}%`,
            backgroundColor: style.color,
            borderRadius: '3px',
            transition: 'width 0.3s ease'
          }}></div>
        </div>
      </div>
    </>
  );
};

export default SearchVolumeCellRenderer;


